import { CoverageCheckResponseType } from "@common-shared/config/coverage.config.ts"
import {
    COMMON_COVERAGE_HISTORY_CONFIG,
    CoverageHistoryMode,
} from "@common-shared/config/coverageHistory.config.ts"
import {
    PoliciesListType,
    PolicyAssignmentStatus,
} from "@common-shared/config/oneVerify/oneVerifyPolicies.config.ts"
import {
    IPoliciesListFiltersData,
    IPolicyColumnConfiguration,
    IPolicyColumnFlaggedField,
} from "@common-shared/types/policies.types.ts"
import { IOVGetCoverageGeneralData } from "@common-shared/types/requestData/oneVerifyRequestData.types.ts"
import {
    IOVCoverageResultDemographics,
    IOneVerifyCoverageCheckHistory,
} from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import { BrowserStorageHelper } from "@front-shared/helpers/browserStorage.helper.ts"

import { REDACTED_VALUE } from "../config/amplitude.config"
import { CHECKER_CONFIG } from "../config/checker.config"
import { COVERAGE_CONFIG } from "../config/coverage.config.tsx"
import { GENERAL_CONFIG } from "../config/general.config.ts"
import { OVERRIDES_CONFIG } from "../config/overrides.config"
import { POLICIES_CONFIG } from "../config/policies.config"
import { ROUTES_CONFIG } from "../config/routes.config"

export class PoliciesHelper {
    /**
     * Get history mode based on list type
     */
    static getHistoryModeBasedOnListType(
        listType: PoliciesListType,
    ): CoverageHistoryMode {
        return (
            BrowserStorageHelper.get<CoverageHistoryMode>(
                GENERAL_CONFIG.browserStorageKeys.coverageHistoryMode,
            ) ||
            COMMON_COVERAGE_HISTORY_CONFIG.defaultHistoryModeByListType[
                listType
            ]
        )
    }

    /**
     * Get single policy base url based on general view type
     */
    static getSinglePolicyBaseUrl(viewType: PoliciesListType): string {
        switch (viewType) {
            case PoliciesListType.REPORTS:
                return ROUTES_CONFIG.reportsSinglePolicyUrl

            case PoliciesListType.HISTORY_SEARCH:
                return ROUTES_CONFIG.searchSinglePolicyUrl

            case PoliciesListType.OVERRIDES:
                return ROUTES_CONFIG.overridesSinglePolicyUrl

            case PoliciesListType.PLAN_YEAR_RESETS:
                return ROUTES_CONFIG.planYearResetsSinglePolicyUrl

            case PoliciesListType.MEDICAID:
                return ROUTES_CONFIG.medicaidReportsSinglePolicyUrl

            case PoliciesListType.AGENTIC:
                return ROUTES_CONFIG.agenticReportsSinglePolicyUrl

            case PoliciesListType.ASSIGNED_TASKS:
            case PoliciesListType.ASSIGNED_TASKS_EXTENDED_CUSTOM_FIELDS:
            case PoliciesListType.ASSIGNED_TASKS_MEDICAID:
                return ROUTES_CONFIG.assignedTasksSinglePolicyUrl

            case PoliciesListType.TASK_ASSIGNMENTS:
            case PoliciesListType.TASK_ASSIGNMENTS_EXTENDED_CUSTOM_FIELDS:
            case PoliciesListType.TASK_ASSIGNMENTS_MEDICAID:
                return ROUTES_CONFIG.taskAssignmentsSinglePolicyUrl
        }
    }

    /**
     * Check if column cell is highlighted
     */
    static isColumnCellHighlighted(
        coverageCheckHistory: IOneVerifyCoverageCheckHistory,
        columnConfig: IPolicyColumnConfiguration,
    ): boolean {
        let result: any = columnConfig.isHighlightedSource
            ? coverageCheckHistory[columnConfig.isHighlightedSource]
            : undefined

        if (!result) {
            return false
        }

        // Go through each value path and assign it's value to result, break down by "." so we can support nested properties of objects
        if (columnConfig.isHighlightedSourcePropertyPath) {
            const initialResult = result

            const pathVariants = Array.isArray(
                columnConfig.isHighlightedSourcePropertyPath,
            )
                ? columnConfig.isHighlightedSourcePropertyPath
                : [columnConfig.isHighlightedSourcePropertyPath]

            for (const path of pathVariants) {
                result = initialResult

                for (const pathPart of path.split(".")) {
                    try {
                        result = result?.[pathPart]
                    } catch (e) {
                        result = undefined
                    }
                }

                if (result) {
                    break
                }
            }
        }

        return typeof result !== "undefined" && result !== null
    }

    /**
     * Check if column cell is flagged
     */
    static isColumnCellFlagged(
        coverageCheckHistory: IOneVerifyCoverageCheckHistory,
        columnConfig: IPolicyColumnConfiguration,
    ): IPolicyColumnFlaggedField | undefined {
        // Early return if no flagged fields to match against
        if (!columnConfig.flaggedFieldOnMatch?.length) {
            return
        }

        // Early return if required configuration is missing
        if (!columnConfig.source || !columnConfig.sourcePropertyPath) {
            return
        }

        // Get the source object with proper type checking
        const sourceObject = coverageCheckHistory[columnConfig.source]

        if (!sourceObject) {
            return
        }

        // Navigate through the property path to get the field value
        let fieldValue: any = sourceObject

        const pathParts = columnConfig.sourcePropertyPath.split(".")

        for (const part of pathParts) {
            if (fieldValue === null || fieldValue === undefined) {
                return
            }

            fieldValue = fieldValue[part]
        }

        // Convert to string and check if it's included in the flagged fields
        const stringValue = String(fieldValue || "").toLowerCase()

        return columnConfig.flaggedFieldOnMatch.find(
            flaggedField => flaggedField.field.toLowerCase() === stringValue,
        )
    }

    /**
     * Get demographics difference between input and result
     */
    static getDemographicsDifference(
        inputData: IOVGetCoverageGeneralData,
        resultDemographics?: IOVCoverageResultDemographics,
    ): Partial<IOVCoverageResultDemographics> {
        if (!resultDemographics) {
            return {}
        }

        const keys = Object.keys(
            resultDemographics,
        ) as (keyof IOVCoverageResultDemographics)[]

        return keys.reduce<Partial<IOVCoverageResultDemographics>>(
            (memo, key) => {
                const inputValue =
                    inputData[key as keyof IOVGetCoverageGeneralData]
                const demographicsValue = resultDemographics[key]

                // Don't process attributes like address and gender
                // Check if input required values were provided
                if (
                    !demographicsValue ||
                    (!inputValue &&
                        CHECKER_CONFIG.demographicsDifference.inputRequiredKeys.includes(
                            key,
                        )) ||
                    CHECKER_CONFIG.demographicsDifference.ignoreKeys.includes(
                        key,
                    )
                ) {
                    return memo
                }

                if (
                    inputValue?.toString()?.toLowerCase()?.trim() !==
                    demographicsValue?.toString()?.toLowerCase()?.trim()
                ) {
                    // @ts-ignore
                    memo[key] = demographicsValue
                }

                return memo
            },
            {},
        )
    }

    /**
     * Check if policy is eligible for bulk override
     */
    static isPolicyBulkOverrideEligible(
        coverageCheck: IOneVerifyCoverageCheckHistory,
    ): boolean {
        return (
            !!coverageCheck.coverageResult.responseStatus &&
            OVERRIDES_CONFIG.bulkOverrides.allowedResponseStatusCodes.includes(
                coverageCheck.coverageResult.responseStatus,
            ) &&
            // Overrides are not enabled for agentic checks for now
            !coverageCheck.agenticCheck &&
            // TODO enable for MEDICAID once we support it
            coverageCheck.coverageResult.responseType ===
                CoverageCheckResponseType.COVERAGE
        )
    }

    /**
     * Check if policy is eligible for recheck
     */
    static isPolicyRecheckEligible(
        coverageCheck: IOneVerifyCoverageCheckHistory,
    ): boolean {
        return (
            !!coverageCheck.coverageResult.responseStatus &&
            POLICIES_CONFIG.singlePolicyHistoryAllowedRunCheckStatusCodes.includes(
                coverageCheck.coverageResult.responseStatus,
            ) &&
            COVERAGE_CONFIG.mainCoverageResponseTypes.includes(
                coverageCheck.coverageResult.responseType,
            )
        )
    }

    /**
     * Check if a resultId matches the latest history entry
     */
    static isLatestCheck(
        resultId: string | undefined,
        selectedItemHistory: IOneVerifyCoverageCheckHistory[],
    ): boolean {
        if (!selectedItemHistory.length) {
            return true
        }

        return selectedItemHistory[0].coverageResult.resultId === resultId
    }

    /**
     * Redact sensitive filters data
     */
    static redactSensitiveFiltersData(
        filters: IPoliciesListFiltersData,
    ): IPoliciesListFiltersData {
        return {
            ...filters,
            memberFirstName: filters.memberFirstName
                ? REDACTED_VALUE
                : undefined,
            memberLastName: filters.memberLastName ? REDACTED_VALUE : undefined,
            memberDob: filters.memberDob ? REDACTED_VALUE : undefined,
            memberId: filters.memberId ? REDACTED_VALUE : undefined,
            patientId: filters.patientId ? REDACTED_VALUE : undefined,
        }
    }

    /**
     * Check if assignment status transition is allowed
     */
    static isStatusTransitionAllowed(
        fromStatus: PolicyAssignmentStatus,
        toStatus: PolicyAssignmentStatus,
    ): boolean {
        // Only allow transitions if the current status is an active status
        return (
            POLICIES_CONFIG.activeAssignmentStatuses.includes(fromStatus) &&
            !POLICIES_CONFIG.internalAssignmentStatuses.includes(toStatus)
        )
    }
}
